import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PulsingLoader } from '@/components/ui/pulsing-loader';
import { usePermissions } from '@/hooks/usePermissions';

interface ProtectedRouteProps {
  /** Permission codes; by default any one of them is enough */
  perms: string[];
  requireAll?: boolean;
  children: ReactNode;
}

/** Blocks a route the user has no permission for. Authentication itself is handled by the layout. */
export function ProtectedRoute({ perms, requireAll = false, children }: ProtectedRouteProps) {
  const { canAny, can, isLoading, isError, refetch } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <PulsingLoader size="md" label="Cargando..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-center">
        <AlertTriangle className="size-8 text-warning" />
        <p className="font-semibold">No se pudieron cargar tus permisos</p>
        <Button variant="outline" onClick={() => void refetch()}>Reintentar</Button>
      </div>
    );
  }

  const allowed = requireAll ? perms.every((p) => can(p)) : canAny(...perms);
  if (allowed) return <>{children}</>;

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-center">
      <ShieldX className="size-8 text-muted-foreground" />
      <p className="text-lg font-semibold">Acceso denegado</p>
      <p className="text-sm text-muted-foreground">No tienes permisos para ver esta página.</p>
      <Button variant="outline" asChild><Link to="/">Ir al inicio</Link></Button>
    </div>
  );
}
